import React, { useState, useEffect, useRef } from 'react';
import { Fragment } from 'react';
import UserEditForm from '../UserEditForm/UserEditForm';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import FitnessHubsOnlyToggle from '../../FitnessProfile/shared/FitnessHubsOnlyToggle';
import DarkModeThemeToggle from '../../FitnessProfile/shared/DarkModeThemeToggle';

export default function UserDetailsCard({
	userId,
	get_user_endpoint,
	update_user_endpoint,
	get_subscriptions_by_user_endpoint,
	create_billing_portal_session_endpoint
}) {
	const { isDarkMode, toggleTheme } = useTheme();
	const { csrfToken, setFitnessHubsOnly, refreshSessionPreferences } = useAuth();
	const modalRef = useRef(null);
	const [
		user,
		setUser
	] = useState({});
	const [
		subscriptions,
		setSubscriptions
	] = useState([]);
	const [
		formData,
		setFormData
	] = useState({
		first_name: user.first_name,
		last_name: user.last_name,
		phone_number: user.phone_number
	});
	const [
		fitnessHubsOnly,
		setFitnessHubsOnlyLocal
	] = useState(false);
	const [
		fitnessHubsOnlySaving,
		setFitnessHubsOnlySaving
	] = useState(false);
	const [
		fitnessHubsOnlyError,
		setFitnessHubsOnlyError
	] = useState('');
	const [
		errorsList,
		setErrorsList
	] = useState([]);
	const [
		fieldErrors,
		setFieldErrors
	] = useState({});
	const [
		isEditing,
		setIsEditing
	] = useState(false);
	const [
		isSubmitted,
		setIsSubmitted
	] = useState(false);
	const [
		isUserLoading,
		setIsUserLoading
	] = useState(false);

	useEffect(
		() => {
			if (get_user_endpoint && userId) {
				setIsUserLoading(true);
				const endpoint = get_user_endpoint.replace('id', userId);
				fetch(endpoint).then((response) => response.json()).then((result) => {
					setUser(result.data || {});
					setFitnessHubsOnlyLocal(Boolean(result.data.fitness_hubs_only));
					setFormData({
						first_name: result.data.first_name,
						last_name: result.data.last_name,
						phone_number: result.data.phone_number
					});
					setIsUserLoading(false);
				});
			}
		},
		[
			userId,
			get_user_endpoint
		]
	);

	useEffect(
		() => {
			if (get_subscriptions_by_user_endpoint && userId) {
				const endpoint = get_subscriptions_by_user_endpoint.replace(
					'user_id',
					userId
				);
				fetch(endpoint).then((response) => response.json()).then((result) => {
					setSubscriptions(result.data || []);
				});
			}
		},
		[
			userId,
			get_subscriptions_by_user_endpoint
		]
	);

	useEffect(
		() => {
			if (get_user_endpoint || get_subscriptions_by_user_endpoint) {
				window.scrollTo(0, 0);
			}
		},
		[
			userId
		]
	);

	const handleFitnessHubsOnlyChange = async (newValue) => {
		setFitnessHubsOnlySaving(true);
		setFitnessHubsOnlyError('');

		try {
			const response = await fetch(update_user_endpoint.replace('id', userId), {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				credentials: 'same-origin',
				body: JSON.stringify({
					user: {
						fitness_hubs_only: newValue
					}
				})
			});

			if (!response.ok) {
				throw new Error('Could not update profile settings.');
			}

			const data = await response.json();
			const savedValue = Boolean(data.data?.fitness_hubs_only ?? newValue);
			setFitnessHubsOnlyLocal(savedValue);
			setUser((prevUser) => ({
				...prevUser,
				fitness_hubs_only: savedValue
			}));
			setFitnessHubsOnly(savedValue);
			await refreshSessionPreferences();
		} catch (error) {
			setFitnessHubsOnlyError(error.message || 'Could not update profile settings.');
		} finally {
			setFitnessHubsOnlySaving(false);
		}
	};

	const handleDarkModeToggle = async () => {
		await toggleTheme();
	};

	const handleManageSubscription = async () => {
		try {
			const response = await fetch(create_billing_portal_session_endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (response.ok) {
				const data = await response.json();
				window.location.href = data.url;
			} else {
				console.error('Failed to create Stripe Billing Portal session');
				alert(
					'Unable to manage your subscription at this time. Please try again later.'
				);
			}
		} catch (error) {
			console.error('Error:', error);
			alert(
				'An error occurred while managing your subscription. Please try again later.'
			);
		}
	};

	const formatPhoneNumber = (phoneNumber) => {
		const cleaned = ('' + phoneNumber).replace(/\D/g, '');
		const match = cleaned.match(/(\d{1,3})(\d{1,3})(\d{1,4})/);
		if (match) {
			return [
				match[1],
				match[2],
				match[3]
			]
				.filter(Boolean)
				.join('-');
		}
		return phoneNumber;
	};

	const capitalizeFirstLetter = (string) => {
		if (!string) return '';
		return string.charAt(0).toUpperCase() + string.slice(1);
	};

	const getOrdinalSuffix = (day) => {
		if (day > 3 && day < 21) return 'th'; // Handles 11th, 12th, 13th, etc.
		switch (day % 10) {
			case 1:
				return 'st';
			case 2:
				return 'nd';
			case 3:
				return 'rd';
			default:
				return 'th';
		}
	};

	const formatDateWithMonthName = (dateString) => {
		const date = new Date(dateString);
		const monthNames = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December'
		];
		const month = monthNames[date.getMonth()];
		const day = date.getDate();
		const year = date.getFullYear();
		const ordinalSuffix = getOrdinalSuffix(day);
		return `${month} ${day}${ordinalSuffix}, ${year}`;
	};

	const mostRecentSubscription = subscriptions.reduce(
		(latest, subscription) => {
			return new Date(subscription.created_at) > new Date(latest.created_at)
				? subscription
				: latest;
		},
		subscriptions[0] || {}
	);

	const hasSubscriptionDetails =
		mostRecentSubscription && mostRecentSubscription.price;

	const onChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevState) => ({
			...prevState,
			[name]: value
		}));
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		try {
			const endpoint = update_user_endpoint.replace('id', user.id);
			const response = await fetch(endpoint, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				credentials: 'same-origin',
				body: JSON.stringify({
					user: formData
				})
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(JSON.stringify(errorData));
			}

			const data = await response.json();
			if (data.success) {
				setUser(data.data);
				setErrorsList([]);
				setFieldErrors({});
				setIsSubmitted(true);
				await refreshSessionPreferences();
			} else {
				setErrorsList(data.message);
			}
		} catch (error) {
			const errorData = JSON.parse(error.message);
			setFieldErrors(errorData.field_errors);
		}
	};

	const closeEditUserModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			closeModal();
		}
	};

	const closeModal = () => {
		setErrorsList([]);
		setFieldErrors({});
		setIsEditing(false);
		setIsSubmitted(false);
		setFormData({
			first_name: user.first_name,
			last_name: user.last_name,
			phone_number: user.phone_number
		});
	};

	useEffect(
		() => {
			if (isEditing) {
				document.addEventListener(
					'mousedown',
					closeEditUserModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closeEditUserModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closeEditUserModalOnClickOutside
				);
			};
		},
		[
			closeModal // This line triggers the form state to update when the modal is closed. Do not change or remove.
		]
	);

	return (
		<div>
			<div className="bg-white dark:bg-dark-card w-full min-w-96 p-5 rounded-md shadow border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text">
				{isUserLoading ? (
					<LoadingSpinner />
				) : (
					<div className="grid lg:grid-cols-12 lg:gap-5">
						<div className="col-span-12 lg:col-span-2">
							<h2 className="text-lg lg:text-xl font-semibold mb-2 text-gray-800 dark:text-dark-text">
								User Profile
							</h2>
						</div>
						<div className="col-span-12 lg:col-span-10">
							<Fragment>
								<div className="border border-gray-200 dark:border-dark-border p-4 bg-gray-50 dark:bg-dark-bg shadow-sm rounded-md relative">
									<span
										className="absolute top-4 right-4 hover:underline cursor-pointer text-gray-700 dark:text-dark-text"
										onClick={() => setIsEditing(true)}
									>
										Edit
									</span>
									<div className="grid md:grid-cols-12">
										{/* Left Column */}
										<div className="md:col-span-6">
											<div className="font-semibold text-gray-800 dark:text-dark-text">
												First Name
											</div>
											<span className="my-1 mr-3 text-gray-700 dark:text-dark-text">
												{user.first_name}
											</span>
											<div className="font-semibold mt-4 text-gray-800 dark:text-dark-text">
												Last Name
											</div>
											<span className="mr-2 block text-gray-700 dark:text-dark-text">
												{user.last_name}
											</span>
											<div className="font-semibold mt-4 text-gray-800 dark:text-dark-text">
												Phone Number
											</div>
											<span className="mr-2 block text-gray-700 dark:text-dark-text">
												{formatPhoneNumber(user.phone_number)}
											</span>

											{/* Stack email and password below phone number on small screens */}
											<div className="md:hidden">
												<div className="font-semibold mt-4 text-gray-800 dark:text-dark-text">
													Email (Username)
												</div>
												<span className="mr-2 block text-gray-700 dark:text-dark-text">
													{user.email}
												</span>
												<div className="font-semibold mt-4 text-gray-800 dark:text-dark-text">
													Password
												</div>
												<span className="mr-2 block text-gray-700 dark:text-dark-text">
													******
												</span>
											</div>
										</div>

										{/* Right Column (hidden on small screens, visible on medium and larger screens) */}
										<div className="md:col-span-6 mt-4 md:mt-0 hidden md:block">
											<div className="font-semibold text-gray-800 dark:text-dark-text">
												Email (Username)
											</div>
											<span className="mr-2 block text-gray-700 dark:text-dark-text">
												{user.email}
											</span>
											<div className="font-semibold mt-4 text-gray-800 dark:text-dark-text">
												Password
											</div>
											<span className="mr-2 block text-gray-700 dark:text-dark-text">
												******
											</span>
										</div>

										{/* Delineation Line */}
										<div className="col-span-12">
											<hr className="my-4 border-gray-200 dark:border-dark-border" />
										</div>

										<div className="col-span-12">
											<div className="font-semibold text-gray-800 dark:text-dark-text">
												Profile settings
											</div>
											<div className="mt-3 space-y-3">
												<FitnessHubsOnlyToggle
													checked={fitnessHubsOnly}
													onChange={handleFitnessHubsOnlyChange}
													disabled={fitnessHubsOnlySaving}
												/>
												{fitnessHubsOnlyError && (
													<p className="text-sm text-red-600 dark:text-red-400" role="alert">
														{fitnessHubsOnlyError}
													</p>
												)}
												<DarkModeThemeToggle
													checked={isDarkMode}
													onChange={handleDarkModeToggle}
												/>
											</div>
										</div>
									</div>
								</div>
								

								{/* Subscription Details Section */}
								<div className="border border-gray-200 dark:border-dark-border p-4 bg-gray-50 dark:bg-dark-bg shadow-sm rounded-md mt-4 relative">
									<div className="grid md:grid-cols-2">
										<div className="md:col-span-1">
											<div className="font-semibold text-gray-800 dark:text-dark-text">
												Subscription Plan
											</div>
											<span className="block my-1 text-gray-700 dark:text-dark-text">
												{hasSubscriptionDetails &&
												mostRecentSubscription.product ? (
													mostRecentSubscription.product.name
												) : (
													'None'
												)}
											</span>
											<div className="font-semibold mt-4 text-gray-800 dark:text-dark-text">
												Status
											</div>
											<span className="block my-1 text-gray-700 dark:text-dark-text">
												{hasSubscriptionDetails &&
												mostRecentSubscription.status ? (
													<Fragment>
														{capitalizeFirstLetter(
															mostRecentSubscription.status
														)}
														{mostRecentSubscription.status !== 'canceled' &&
														mostRecentSubscription.scheduled_cancellation_date && (
															<Fragment
															>{`, but cancels on ${formatDateWithMonthName(
																mostRecentSubscription.scheduled_cancellation_date
															)}`}</Fragment>
														)}
													</Fragment>
												) : (
													'N/A'
												)}
											</span>
										</div>
										<div className="md:col-span-1">
											{hasSubscriptionDetails && (
												<div>
													<div className="font-semibold mt-4 md:mt-0 text-gray-800 dark:text-dark-text">
														Amount
													</div>
													<div className="flex items-center">
														<span className="block my-1 text-gray-700 dark:text-dark-text">
															{mostRecentSubscription.final_price_cents ===
															0 ? (
																'$0'
															) : mostRecentSubscription.final_price_cents ? (
																`$${mostRecentSubscription.final_price_cents /
																	100}`
															) : (
																'Contact Support'
															)}/
															{mostRecentSubscription.billing_frequency}
															{mostRecentSubscription.discount_name ? (
																<span className="ml-2">
																	({mostRecentSubscription.discount_name})
																</span>
															) : (
																''
															)}
														</span>
													</div>
												</div>
											)}
										</div>
										<div className="mt-4 xl:mt-0 xl:absolute xl:top-4 xl:right-4">
											{hasSubscriptionDetails ? (
												<button
													onClick={handleManageSubscription}
													className="w-full xl:w-auto px-4 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg"
												>
													Manage subscription
												</button>
											) : (
												<a
													href="/pricing"
													className="w-full xl:w-auto px-4 py-2 bg-blue hover:bg-blue-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-lg"
												>
													Add subscription
												</a>
											)}
										</div>
									</div>
								</div>
							</Fragment>
						</div>
					</div>
				)}
				{isEditing && (
					<UserEditForm
						formData={formData}
						modalRef={modalRef}
						closeModal={closeModal}
						onChange={onChange}
						onSubmit={onSubmit}
						errorsList={errorsList}
						fieldErrors={fieldErrors}
						isSubmitted={isSubmitted}
					/>
				)}
			</div>

		</div>
	);
}
